import {type ReactNode, useCallback, useMemo} from "react";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ThreadMessage,
  type ThreadUserMessage,
  type ThreadAssistantMessage,
  type ThreadHistoryAdapter,
  type TextMessagePart,
} from "@assistant-ui/react";
import {toast} from "sonner";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import * as m from "@/paraglide/messages";
import {createPersonaChatAdapter} from "@/lib/persona-chat-runtime";
import {
  getAiPersonaMessages,
  addAiPersonaMessage,
  clearAiPersonaMessages,
  type AiPersona,
  type EventContext,
  AiPersonaChatMessagesResponse,
} from "@/lib/personas-api";

interface PersonaChatRuntimeProviderProps {
  profile: AiPersona;
  locationId?: string | null;
  date?: string | null;
  /** AI persona database ID - required for chat persistence */
  aiPersonaId: string;
  children: (props: {onClearHistory: () => void}) => ReactNode;
}

/**
 * Type guard to check if content is a text part
 */
function isTextContent(content: {type: string}): content is TextMessagePart {
  return content.type === "text";
}

/**
 * Extract text content from a ThreadMessage.
 * Handles the complex union types in assistant-ui.
 */
function extractTextFromMessage(message: ThreadMessage): string {
  return message.content
    .filter(isTextContent)
    .map((c) => c.text)
    .join("\n");
}

type HistoryMessage = {
  message: ThreadMessage;
  parentId: string | null;
};

/**
 * Convert API messages to assistant-ui history format with parent-child relationships.
 * Combines message transformation and parentId assignment in a single pass.
 */
function responseToHistoryMessages(
  messages?: AiPersonaChatMessagesResponse["messages"]
): HistoryMessage[] {
  if (!messages) return [];

  let previousId: string | null = null;

  return messages.map((msg) => {
    const textContent: TextMessagePart[] = [{type: "text", text: msg.content}];

    const message: ThreadMessage =
      msg.role === "user"
        ? ({
            id: msg.id,
            createdAt: new Date(msg.created_at),
            role: "user",
            content: textContent,
            attachments: [],
            metadata: {custom: {}},
          } satisfies ThreadUserMessage)
        : ({
            id: msg.id,
            createdAt: new Date(msg.created_at),
            role: "assistant",
            content: textContent,
            status: {type: "complete", reason: "stop"},
            metadata: {
              unstable_state: null,
              unstable_annotations: [],
              unstable_data: [],
              steps: [],
              custom: {},
            },
          } satisfies ThreadAssistantMessage);

    const result = {message, parentId: previousId};
    previousId = msg.id;
    return result;
  });
}

/**
 * Provider component that sets up the persona chat runtime with database persistence.
 * Uses ThreadHistoryAdapter for proper message persistence.
 *
 * Key implementation details:
 * - historyAdapter is stable via useMemo closure (no refs needed)
 * - load() is called once when the thread initializes
 * - append() is called for each new message (both user and assistant)
 * - No optimistic updates in storage hook prevents infinite loops
 */
export function PersonaChatRuntimeProvider({
  profile,
  locationId,
  date,
  aiPersonaId,
  children,
}: PersonaChatRuntimeProviderProps) {
  const queryClient = useQueryClient();

  // Query key for cache operations
  const queryKey = useMemo(() => ["ai-persona-messages", aiPersonaId], [aiPersonaId]);

  // Add message mutation (no optimistic updates - assistant-ui manages display)
  const {mutateAsync: addMessageAsync} = useMutation({
    mutationFn: ({role, content}: {role: "user" | "assistant"; content: string}) =>
      addAiPersonaMessage(aiPersonaId, {role, content}),
  });

  // Clear history mutation
  const clearMutation = useMutation({
    mutationFn: () => clearAiPersonaMessages(aiPersonaId),
    onSuccess: () => queryClient.setQueryData(queryKey, {messages: [], total: 0}),
  });

  // Create event context for the chat
  const context: EventContext | undefined = useMemo(() => {
    if (!locationId && !date) return undefined;
    return {
      location: locationId || undefined,
      date: date || undefined,
      enable_mention_retrieval: true,
    };
  }, [locationId, date]);

  // Create the chat adapter - recreate when profile or context changes
  const adapter = useMemo(
    () =>
      createPersonaChatAdapter({
        profile,
        context,
      }),
    [profile, context]
  );

  // Create history adapter - fetches messages internally via queryClient.fetchQuery()
  // This allows the load() method to be self-contained and use TanStack Query's cache
  const historyAdapter = useMemo((): ThreadHistoryAdapter => {
    return {
      async load() {
        // Fetch directly using queryClient (non-hook API)
        // This respects staleTime and uses the cache
        const data = await queryClient.fetchQuery({
          queryKey: ["ai-persona-messages", aiPersonaId] as const,
          queryFn: () => getAiPersonaMessages(aiPersonaId),
          staleTime: 30000,
        });

        return {
          messages: responseToHistoryMessages(data?.messages),
        };
      },
      async append({message}: {message: ThreadMessage; parentId: string | null}) {
        const content = extractTextFromMessage(message);

        // Only persist non-empty messages
        if (!content.trim()) return;

        // For assistant messages, only persist if complete (not running/cancelled)
        if (message.role === "assistant") {
          const status = (message as ThreadAssistantMessage).status;
          if (status?.type !== "complete") {
            return;
          }
        }

        // Persist to database - assistant-ui manages display state
        try {
          await addMessageAsync({
            role: message.role as "user" | "assistant",
            content,
          });
        } catch (err) {
          console.error("[PersonaChat] Failed to persist message:", err);
          toast.error(m.persona_chat_save_error());
        }
      },
    };
  }, [queryClient, aiPersonaId, addMessageAsync]);

  // Create the runtime with history adapter
  const runtime = useLocalRuntime(adapter, {
    adapters: {history: historyAdapter},
  });

  // Handler to clear history and reset runtime
  const handleClearHistory = useCallback(async () => {
    await clearMutation.mutateAsync();
    runtime.thread.reset();
  }, [clearMutation, runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children({onClearHistory: handleClearHistory})}
    </AssistantRuntimeProvider>
  );
}
